import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DailyReading } from "../../constants/dashboardStats";
import "./StudentActivityChart.scss";

type StudentActivityChartProps = {
  data: DailyReading[];
};

type TooltipContentProps = {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; color: string }>;
  label?: string;
};

const SERIES_LABELS: Record<string, string> = {
  questions: "Total",
  correct: "Correct",
  incorrect: "Incorrect",
};

const ChartTooltip = ({ active, payload, label }: TooltipContentProps) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="student-activity-chart__tooltip">
      <p className="student-activity-chart__tooltip-title">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} className="student-activity-chart__tooltip-row">
          <span>{SERIES_LABELS[entry.dataKey] ?? entry.dataKey}</span>
          <span>{entry.value}</span>
        </p>
      ))}
    </div>
  );
};

const StudentActivityChart = ({ data }: StudentActivityChartProps) => {
  const showDots = data.length <= 7;

  return (
    <div className="student-activity-chart">
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="rgba(150,150,150,0.1)" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 15, fill: "rgba(17, 24, 39, 0.55)" }}
            tickLine={false}
            axisLine={false}
            interval={data.length > 15 ? Math.floor(data.length / 8) : 0}
            angle={data.length > 7 ? -45 : 0}
            textAnchor={data.length > 7 ? "end" : "middle"}
            height={data.length > 7 ? 50 : 30}
          />
          <YAxis
            tick={{ fontSize: 15, fill: "rgba(17, 24, 39, 0.55)" }}
            tickLine={false}
            axisLine={false}
            width={36}
            allowDecimals={false}
          />
          <Tooltip
            isAnimationActive={false}
            content={<ChartTooltip />}
            cursor={{ stroke: "rgba(27, 94, 166, 0.2)", strokeWidth: 1 }}
          />
          <Line
            type="monotone"
            dataKey="questions"
            stroke="#1B5EA6"
            strokeWidth={2}
            dot={showDots ? { r: 4, fill: "#1B5EA6" } : { r: 2, fill: "#1B5EA6" }}
            activeDot={{ r: 5 }}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="correct"
            stroke="#1D9E75"
            strokeWidth={2}
            strokeDasharray="5 3"
            dot={showDots ? { r: 4, fill: "#1D9E75" } : { r: 2, fill: "#1D9E75" }}
            activeDot={{ r: 5 }}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="incorrect"
            stroke="#D85A30"
            strokeWidth={2}
            strokeDasharray="2 2"
            dot={showDots ? { r: 4, fill: "#D85A30" } : { r: 2, fill: "#D85A30" }}
            activeDot={{ r: 5 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default StudentActivityChart;
